import { HttpClient } from '@angular/common/http';
import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { Navigation } from 'app/core/navigation/navigation.types';
import { finalize, Observable, ReplaySubject, shareReplay, tap } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { CredentialsCurrentUser, CredentialsService } from '..';
import { UserAuthService } from '../user/user.service';
import { environment } from '@env/environment';
import { ROLES } from '@app/constants';

@Injectable({ providedIn: 'root' })
export class NavigationService {
    private _httpClient = inject(HttpClient);
    private _navigation = new ReplaySubject<Navigation>(1);
    private _credentialsService = inject(CredentialsService);
    private _userAuthService = inject(UserAuthService);
    private _destroyRef = inject(DestroyRef);
    private currentUser = signal<CredentialsCurrentUser>(null);
    private _lastKnownUser: CredentialsCurrentUser | null = null;
    private environment = environment;
    private _role: string;
    private _isPop: boolean;
    private _isPremium: boolean;
    private _isPrinted: boolean;
    private _isProcter: boolean;
    private _isDidi: boolean;
    private _hasMercado: boolean;
    private _hasAmazon: boolean;
    private _publicDashbaords: boolean;
    private _lastNavigationUserKey: string | undefined = undefined;
    private _pendingGet$: Observable<Navigation> | null = null;

    constructor() {
        this._userAuthService.user$
            .pipe(
                distinctUntilChanged(
                    (prev, next) =>
                        this.userSessionKey(prev) === this.userSessionKey(next)
                ),
                takeUntilDestroyed(this._destroyRef)
            )
            .subscribe(user => {
                this._lastKnownUser = user ?? null;
                const userKey = this.userSessionKey(user);
                if (this._lastNavigationUserKey === userKey) {
                    return;
                }
                this._lastNavigationUserKey = userKey;
                this.get(user ?? null).subscribe();
            });
    }

    private userSessionKey(user: CredentialsCurrentUser | null): string {
        return user?.id ?? 'guest';
    }

    private resolveUser(
        userOverride?: CredentialsCurrentUser | null
    ): CredentialsCurrentUser | null {
        if (userOverride !== undefined) {
            return userOverride;
        }
        return (
            this._credentialsService.credentialsCurrentUser ??
            this._lastKnownUser
        );
    }

    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    get(userOverride?: CredentialsCurrentUser | null): Observable<Navigation> {
        if (this._pendingGet$) {
            return this._pendingGet$;
        }

        this._pendingGet$ = this._httpClient
            .get<Navigation>('api/common/navigation')
            .pipe(
                tap(navigation => {
                    const user = this.resolveUser(userOverride);
                    this.currentUser.set(user);
                    this.fillPermissions();
                    this._navigation.next(this.filterNavigation(navigation));
                }),
                finalize(() => {
                    this._pendingGet$ = null;
                }),
                shareReplay(1)
            );

        return this._pendingGet$;
    }

    fillPermissions() {
        const user = this.currentUser();
        if (!user) {
            // Usuario no autenticado: usar valores por defecto
            this._role = null;
            this._isPop = false;
            this._isPremium = false;
            this._isPrinted = false;
            this._isProcter = false;
            this._isDidi = false;
            this._hasMercado = false;
            this._hasAmazon = false;
            this._publicDashbaords = false;
            return;
        }

        this._role = user.role;
        if (
            this._role === ROLES.PROVEEDOR ||
            this._role === ROLES.COMPRAS_SR ||
            this._role === ROLES.COMPRAS
        ) {
            if (user?.company) {
                this._isPop = user.company['isPop'];
                this._isPremium = user.company['isPremium'];
                this._isPrinted = user.company['isPrinted'];
                this._hasMercado = user.company['hasMercado'];
                this._hasAmazon = user.company['hasAmazon'];
                this._publicDashbaords = user.company['publicDashbaords'];
            }
        }
        if (this._role === ROLES.COMPRAS || this._role === ROLES.COMPRAS_SR) {
            this._isProcter =
                user?.clientId === environment.coupaClients.procter.id
                    ? true
                    : false;
            this._isDidi =
                user?.clientId === environment.coupaClients.didi.id
                    ? true
                    : false;
        }
    }

    filterNavigation(navigation: Navigation) {
        // Si el usuario no está autenticado, solo mostrar 'home' y 'premiums'
        if (!this._role) {
            const homePage = navigation.default.find(x => x.id === 'home');
            const premiumsPage = navigation.default.find(
                x => x.id === 'premiums'
            );

            const publicPages = [];

            // Agregar 'home' sin verificar permisos
            if (homePage) {
                publicPages.push({
                    ...homePage,
                    permissions: undefined // Remover restricciones de permisos
                });
            }

            // Para premiums, solo mostrar el catálogo sin verificar permisos
            if (premiumsPage && premiumsPage.children) {
                const catalogueChild = premiumsPage.children.find(
                    c => c.id === 'premiums.catalogue'
                );
                if (catalogueChild) {
                    publicPages.push({
                        ...premiumsPage,
                        permissions: undefined, // Remover restricciones de permisos del padre
                        children: [
                            {
                                ...catalogueChild,
                                permissions: undefined // Remover restricciones de permisos del hijo
                            }
                        ]
                    });
                }
            }

            return {
                compact: publicPages,
                default: publicPages,
                horizontal: publicPages,
                futuristic: publicPages
            };
        }

        const pages = this.filterNavigationItems(navigation.default)
            .filter(x => {
                if (
                    this._role === ROLES.PROVEEDOR ||
                    this._role === ROLES.COMPRAS ||
                    this._role === ROLES.COMPRAS_SR
                ) {
                    if (this._isPop && x.title === 'POP') {
                        return x;
                    }
                    if (this._isPremium && x.title === 'Premiums') {
                        return x;
                    }
                    if (this._isPrinted && x.title === 'Impresos') {
                        return x;
                    }
                    if (
                        this._role !== ROLES.PROVEEDOR &&
                        this._hasMercado &&
                        x.title === 'Mercadolibre'
                    ) {
                        return x;
                    }
                    if (
                        (this._role !== ROLES.PROVEEDOR &&
                            this._hasAmazon &&
                            x.title === 'RFQ Amazon') ||
                        (this._role === ROLES.PROVEEDOR &&
                            x.title === 'RFQ Amazon')
                    ) {
                        return x;
                    }
                    if (
                        this._role === ROLES.COMPRAS &&
                        this._publicDashbaords &&
                        x.title === 'Dashboard'
                    ) {
                        return x;
                    }
                    if (
                        this._role === ROLES.COMPRAS_SR &&
                        x.title === 'Dashboard'
                    ) {
                        return x;
                    }
                    if (
                        x.title !== 'Impresos' &&
                        x.title !== 'Premiums' &&
                        x.title !== 'Mercadolibre' &&
                        x.title !== 'RFQ Amazon' &&
                        x.title !== 'Dashboard' &&
                        x.title !== 'POP'
                    ) {
                        return x;
                    }
                } else {
                    return x;
                }
            })
            .filter(x => {
                if (
                    this._role === ROLES.COMPRAS ||
                    this._role === ROLES.COMPRAS_SR
                ) {
                    if (!this._isProcter && !this._isDidi) {
                        if (
                            // x.title !== 'Impresos' &&
                            x.title !== 'Influencers' &&
                            x.title !== 'Autos' &&
                            x.title !== '¿Necesitas ayuda?' &&
                            x.title !== 'Agencias' &&
                            x.title !== 'SBD' &&
                            x.title !== 'RFP'
                        ) {
                            return x;
                        }
                    } else {
                        return x;
                    }
                } else {
                    return x;
                }
            });
        return {
            compact: pages,
            default: pages,
            horizontal: pages,
            futuristic: pages
        };
    }

    /**
     * Filtra ítems por rol. Si un hijo no declara `permissions`, hereda las del padre.
     * Recorre collapsables/groups en profundidad.
     */
    private filterNavigationItems(
        items: FuseNavigationItem[],
        parentPermissions?: string[]
    ): FuseNavigationItem[] {
        if (!items?.length) {
            return [];
        }

        return items
            .map(item => {
                const effectivePermissions =
                    item.permissions ?? parentPermissions;
                const permissionsForChildren =
                    item.permissions ?? parentPermissions;

                if (item.children?.length) {
                    const children = this.filterNavigationItems(
                        item.children,
                        permissionsForChildren
                    );

                    if (children.length === 0) {
                        return null;
                    }

                    // Contenedor sin permisos propios ni heredados: visible si quedan hijos
                    if (
                        effectivePermissions &&
                        !effectivePermissions.includes(this._role)
                    ) {
                        return null;
                    }

                    return { ...item, children };
                }

                // Hoja: requiere permisos efectivos que incluyan el rol
                if (
                    !effectivePermissions ||
                    !effectivePermissions.includes(this._role)
                ) {
                    return null;
                }

                return { ...item };
            })
            .filter((item): item is FuseNavigationItem => item !== null);
    }
}
